import { TaskCommentText } from "./TaskCommentText";
import {
  getAssignmentAppsOptions,
  useAddMatterTaskUserAssignments,
  useRemoveMatterTaskUserAssignments,
} from "@/lib/data/react-query/matters/matter-task";
import { useAppMentionOptions } from "@/hooks/use-app-mention-options";
import { useUserDirectory } from "@/hooks/use-user-directory";
import MattersSearchBar from "@/components/features/matters/MattersSearchBar/MattersSearchBar";
import { MattersTable } from "@/components/features/matters/MattersTable/MattersTable";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  CheckboxFormField,
  DateFormField,
  Form,
  FormSubmitButton,
  LoadingSpinner,
  MultiselectFormField,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { UserProfilePhoto } from "@/components/ui/UserProfilePhoto/UserProfilePhoto";
import { useUser } from "@/components/UserProvider";
import {
  type MatterTaskWithRoles,
  useTasksViewModel,
} from "@/hooks/matter-view-models/use-tasks-view-model";
import { useSearchObject } from "@/hooks/useSearchObject";
import { useTiptapMentions } from "@/hooks/useTiptapMentions";
import {
  getUsersOptions,
  useCreateMatterTask,
  useCreateMatterUserConnection,
  useDeleteMatterTask,
  useDeleteMatterUserConnection,
  useUpdateMatterTask,
} from "@/lib/data/react-query";
import { retrieveMassTortQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { retrieveMatterTasksQueryOptions } from "@/lib/data/react-query/matters/matter-task";
import {
  useCreateMatterTaskComment,
  useDeleteMatterTaskComment,
  useUpdateMatterTaskComment,
} from "@/lib/data/react-query/matters/matter-task-comment";
import { type MatterSearchResult } from "@/lib/data/requests";
import { MatterTaskSchema } from "@/lib/data/schemas";
import { MATTER_TASK_PRIORITY_OPTIONS } from "@/lib/data/static/matters/taskStatics";
import { MATTER_TASK_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type MatterSubItemType, type MatterTask } from "@/lib/types";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDateTime, formatUsersName } from "@/lib/utils/format";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { getMassTortAnnotationMatter } from "@/lib/utils/massTortAnnotationUtils";
import { convertBackendMessageToDisplay } from "@/lib/utils/mentionUtils";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import type { Row } from "@tanstack/react-table";
import { EditorContent } from "@tiptap/react";
import dayjs from "dayjs";
import { Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import {
  buildTaskUserOptions,
  type TaskUserOption,
} from "../task-user-options";

function MatterTaskCreateMatterPicker({
  modalStackId,
}: {
  modalStackId: string;
}) {
  const { updateSlideoverPayload } = useModals();
  const { searchObject, addSearchObjectValue, removeSearchObjectValue } =
    useSearchObject({
      defaultSearchObject: {},
      itemType: "matter",
    });

  const handleRowClick = (row: Row<MatterSearchResult>) => {
    const selectedMatterId = row.original.id;
    if (!selectedMatterId) {
      return;
    }
    updateSlideoverPayload(modalStackId, { matterId: selectedMatterId });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 px-5 pt-3 pb-4">
      <div className="border-border shrink-0 space-y-2 border-b pb-4">
        <Typography variant="heading" className="text-lg">
          Choose the matter for this task
        </Typography>
        <Typography className="text-muted-foreground text-sm leading-relaxed">
          Your new task will be created on whichever matter you select below
        </Typography>
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-auto">
        <MattersTable
          searchBar={
            <MattersSearchBar
              searchObject={searchObject}
              addSearchObjectValue={addSearchObjectValue}
              removeSearchObjectValue={removeSearchObjectValue}
            />
          }
          tableName={tanstackTableNames.dashboardCreateTaskMatters}
          searchObject={searchObject}
          onRowClick={handleRowClick}
        />
      </div>
    </div>
  );
}

interface AddOrEditMatterTaskModalProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  data?: MatterTask;
  matterId?: string;
  massTortId?: string;
  /** Stack entry id from `ModalProvider` / `GlobalModalManager` for `updateSlideoverPayload`. */
  modalStackId?: string;
  onDelete?: () => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideMatterField?: boolean;
  subItem?: {
    id: string;
    type: MatterSubItemType;
  };
  activeMatterTaskId?: string;
  initialData?: Partial<MatterTask>;
}

const formSchema = MatterTaskSchema.pick({
  title: true,
  description: true,
  status: true,
  priority: true,
  startDate: true,
  dueDate: true,
  completedDate: true,
})
  .extend({
    assigneeIds: z.array(z.string()).default([]),
    appAssigneeIds: z.array(z.string()).default([]),
    assignerIds: z.array(z.string()).default([]),
    notifyMe: z.boolean().optional(),
  })
  .refine(
    (data) => {
      // If both dates are provided, start date must be before due date
      if (data.startDate && data.dueDate) {
        return dayjs(data.startDate).isBefore(dayjs(data.dueDate));
      }
      return true;
    },
    {
      message: "Start date must be before due date",
      path: ["startDate"], // This will show the error on the start date field
    },
  );

const defaultObject = {
  title: "",
  description: "",
  status: "Open" as const,
  priority: undefined,
  startDate: undefined,
  dueDate: undefined,
  completedDate: undefined,
  assigneeIds: [],
  appAssigneeIds: [],
  assignerIds: [],
  notifyMe: false,
};

export const AddOrEditMatterTaskModal = ({
  open,
  setOpen,
  title,
  description,
  activeMatterTaskId,
  editing,
  matterId,
  massTortId,
  hasEditPermission,
  hasDeletePermission,
  hideMatterField,
  subItem,
  initialData,
  modalStackId,
}: AddOrEditMatterTaskModalProps) => {
  const taskModalFormId = "add-or-edit-matter-task-modal-form";
  const isMatterPickerStep = !editing && !matterId && !massTortId;
  const hasParentContext = Boolean(matterId || massTortId);
  const {
    data: matterData,
    isError: isMatterError,
    isPending: isMatterPending,
    isFetching: isMatterFetching,
  } = useQuery({
    ...retrieveMatterQueryOptions(matterId ?? ""),
    enabled: Boolean(matterId && !massTortId),
  });
  const {
    data: massTortData,
    isError: isMassTortError,
    isPending: isMassTortPending,
    isFetching: isMassTortFetching,
  } = useQuery({
    ...retrieveMassTortQueryOptions(massTortId ?? ""),
    enabled: Boolean(massTortId),
  });
  const targetTaskId = activeMatterTaskId ?? initialData?.id;
  const {
    data: matterTasksData,
    isFetching: isTaskFetching,
    isSuccess: isTaskLoaded,
  } = useQuery({
    ...retrieveMatterTasksQueryOptions({
      matterId: matterId ?? "",
      taskId: editing ? targetTaskId : undefined,
      pageSize: editing && targetTaskId ? 1 : 1000,
    }),
    enabled: Boolean(matterId && !massTortId),
  });
  useEffect(() => {
    if (
      open &&
      editing &&
      targetTaskId &&
      !massTortId &&
      isTaskLoaded &&
      !isTaskFetching &&
      matterTasksData.data.length === 0
    ) {
      toast("This task was deleted. The task view has been closed.");
      setOpen(false);
    }
  }, [
    open,
    editing,
    targetTaskId,
    massTortId,
    isTaskLoaded,
    isTaskFetching,
    matterTasksData,
    setOpen,
  ]);
  const matter = massTortId
    ? getMassTortAnnotationMatter(massTortData?.data)
    : matterData?.data;
  const annotationParentPayload = massTortId
    ? { massTortId }
    : { matterId: matter?.id ?? "" };
  const isAnnotationParentError = massTortId ? isMassTortError : isMatterError;
  const isAwaitingMatterDetail =
    Boolean(matterId) &&
    !massTortId &&
    !isMatterError &&
    !matter &&
    (isMatterPending || isMatterFetching);
  const isAwaitingMassTortDetail =
    Boolean(massTortId) &&
    !isMassTortError &&
    !matter &&
    (isMassTortPending || isMassTortFetching);
  const isAwaitingParentDetail = massTortId
    ? isAwaitingMassTortDetail
    : isAwaitingMatterDetail;
  const bodyContained =
    isMatterPickerStep ||
    isAwaitingParentDetail ||
    (hasParentContext && isAnnotationParentError);
  const { updateSlideoverPayload } = useModals();
  const { currentUser, hasActionPermission } = useUser();
  const canCreateMatterUserConnections =
    !massTortId &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matter,
      ACTION_PERMISSION_VALUES.update,
    );
  const taskParentPermissionKey = massTortId
    ? ACTION_PERMISSION_KEYS.massTort
    : ACTION_PERMISSION_KEYS.matter;
  const { allTasks } = useTasksViewModel(matter, matterTasksData?.data);
  const initialMatterTask = useMemo(() => {
    if (!initialData?.id) {
      return undefined;
    }

    const taskWithRoles = initialData as Partial<MatterTaskWithRoles>;
    return {
      ...taskWithRoles,
      assigners: taskWithRoles.assigners ?? [],
      assignees: taskWithRoles.assignees ?? [],
      comments: taskWithRoles.comments ?? [],
      notifiers: taskWithRoles.notifiers ?? [],
    } as MatterTaskWithRoles;
  }, [initialData]);
  const activeMatterTask =
    allTasks.find((task) => task.id === targetTaskId) ?? initialMatterTask;
  const activeMatterTaskWithInitialData = editing
    ? activeMatterTask
    : mergeInitialData(activeMatterTask, initialData);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultObject,
          ...activeMatterTaskWithInitialData,
          appAssigneeIds:
            activeMatterTask?.assignees.flatMap((assignee) =>
              assignee.integrationAppId ? [assignee.integrationAppId] : [],
            ) ?? [],
          assigneeIds:
            activeMatterTask?.assignees.flatMap((assignee) =>
              assignee.userId ? [assignee.userId] : [],
            ) ?? [],
          assignerIds:
            activeMatterTask?.assigners.flatMap((assigner) =>
              assigner.userId ? [assigner.userId] : [],
            ) ?? [],
          notifyMe:
            activeMatterTask?.notifiers.some(
              (notifier) => notifier.userId === currentUser.id,
            ) ?? false,
        }
      : {
          ...defaultObject,
          ...initialData,
          ...(canCreateMatterUserConnections
            ? { assignerIds: [currentUser.id] }
            : {}),
        },
    mode: "onSubmit",
  });

  const { data: usersData, isLoading: isUsersLoading } =
    useQuery(getUsersOptions());
  const rawUsers = usersData?.data;
  const users = useMemo(() => rawUsers ?? [], [rawUsers]);
  const { data: assignmentApps = [] } = useQuery(
    getAssignmentAppsOptions(open && !massTortId),
  );
  const addAppAssignment = useAddMatterTaskUserAssignments();
  const removeAppAssignment = useRemoveMatterTaskUserAssignments();
  const selectedAssigneeIds = form.watch("assigneeIds");
  const selectedAssignerIds = form.watch("assignerIds");
  const userDirectory = useUserDirectory({
    enabled: open,
    selectedIds: [...selectedAssigneeIds, ...selectedAssignerIds],
  });
  const groupedUserOptions = useMemo(() => {
    return buildTaskUserOptions(
      matter?.assignees ?? [],
      userDirectory.users,
      userDirectory.search,
      [...selectedAssigneeIds, ...selectedAssignerIds],
      users,
    );
  }, [
    matter?.assignees,
    selectedAssigneeIds,
    selectedAssignerIds,
    userDirectory.search,
    userDirectory.users,
    users,
  ]);
  const formatTaskUserLabel = useCallback((user?: TaskUserOption) => {
    if (!user) {
      return;
    }
    const name = formatUsersName(user)?.trim();
    if (!name) {
      return user.id;
    }
    return name;
  }, []);
  const taskUserOptions = useMemo(
    () =>
      groupedUserOptions.map((user) => ({
        label: formatTaskUserLabel(user) ?? user.id,
        value: user.id,
        badges: user.roleLabels.length ? user.roleLabels : undefined,
        group: user.group,
      })),
    [formatTaskUserLabel, groupedUserOptions],
  );
  const createMatterTask = useCreateMatterTask();
  const createMatterUserConnection = useCreateMatterUserConnection({
    showToast: false,
  });
  const deleteMatterUserConnection = useDeleteMatterUserConnection({
    showToast: false,
  });
  const updateMatterTask = useUpdateMatterTask();
  const deleteMatterTask = useDeleteMatterTask();
  const createMatterTaskComment = useCreateMatterTaskComment();
  const updateMatterTaskComment = useUpdateMatterTaskComment();
  const deleteMatterTaskComment = useDeleteMatterTaskComment();

  const [isSavingConnections, setIsSavingConnections] = useState(false);
  const isLoading =
    createMatterTask.isPending ||
    createMatterUserConnection.isPending ||
    deleteMatterUserConnection.isPending ||
    updateMatterTask.isPending ||
    deleteMatterTask.isPending ||
    isSavingConnections;

  const canEdit = hasEditPermission;
  const canAddComment = editing ? Boolean(canEdit) : true;

  const isCompleted = form.watch("status") === "Completed";

  // Delete confirmation state
  const [isDeleting, setIsDeleting] = useState(false);
  const [commentInput, setCommentInput] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentInput, setEditCommentInput] = useState("");
  const [commentIdPendingDelete, setCommentIdPendingDelete] = useState<
    string | null
  >(null);

  const mentionOptions = useAppMentionOptions(
    users,
    !massTortId,
    activeMatterTask?.comments.flatMap(
      (comment) => comment.mentionedApps ?? [],
    ),
  );

  const {
    editor: commentEditor,
    getBackendMessage: getCommentBackendMessage,
    setContent: setCommentContent,
    mentionDropdownOpenRef,
  } = useTiptapMentions({
    mentionOptions,
    isLoading: isUsersLoading,
    onUpdate: (value) => {
      setCommentInput(value);
    },
    isEditable: canAddComment,
    className:
      "bg-background max-h-[96px] border-0 px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
    placeholder: "Add a comment...",
  });

  const {
    editor: editCommentEditor,
    getBackendMessage: getEditCommentBackendMessage,
    setContent: setEditCommentContent,
    mentionDropdownOpenRef: editMentionDropdownOpenRef,
  } = useTiptapMentions({
    mentionOptions,
    isLoading: isUsersLoading,
    onUpdate: (value) => {
      setEditCommentInput(value);
    },
    isEditable: Boolean(editingCommentId),
    className:
      "bg-background max-h-[96px] border-0 px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
    placeholder: "Edit comment...",
  });

  const handleAddComment = async () => {
    if (!commentInput.trim()) {
      return;
    }

    if (!editing || !activeMatterTask || !matter) {
      toast.error("Save the task before adding comments.");
      return;
    }

    const previousCommentInput = commentInput;
    const commentMessage = getCommentBackendMessage();

    setCommentContent("");
    setCommentInput("");

    try {
      await createMatterTaskComment.mutateAsync({
        ...annotationParentPayload,
        taskId: activeMatterTask.id,
        message: commentMessage,
        user: currentUser,
      });
    } catch (error) {
      setCommentContent(commentMessage);
      setCommentInput(previousCommentInput);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Unable to add comment. Please try again.");
      }
    }
  };

  const sortedComments = useMemo(() => {
    if (!activeMatterTask?.comments) {
      return [];
    }

    return [...activeMatterTask.comments].sort((a, b) =>
      dayjs(a.dateCreated).diff(dayjs(b.dateCreated)),
    );
  }, [activeMatterTask?.comments]);

  const commentCount = sortedComments.length;

  const canEditOtherComments = hasActionPermission(
    taskParentPermissionKey,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteOtherComments = hasActionPermission(
    taskParentPermissionKey,
    ACTION_PERMISSION_VALUES.update,
  );

  const canSaveEditedComment =
    Boolean(editingCommentId) &&
    editCommentInput.trim().length > 0 &&
    !updateMatterTaskComment.isPending &&
    (() => {
      if (!editingCommentId || !activeMatterTask) {
        return false;
      }
      const existingComment = activeMatterTask.comments.find(
        (comment) => comment.id === editingCommentId,
      );
      if (!existingComment) return false;

      const isOwnComment = existingComment.userId === currentUser.id;
      if (isOwnComment) return true;

      return canEditOtherComments;
    })();

  const handleStartEditComment = (comment: (typeof sortedComments)[number]) => {
    const displayMessage =
      mentionOptions.length > 0
        ? convertBackendMessageToDisplay(comment.message, mentionOptions)
        : comment.message;

    setEditingCommentId(comment.id);
    setEditCommentInput(displayMessage);
    setEditCommentContent(comment.message);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentInput("");
    setEditCommentContent("");
  };

  const handleSaveEditedComment = async () => {
    if (
      !editingCommentId ||
      !activeMatterTask ||
      !editCommentInput.trim() ||
      !matter
    ) {
      return;
    }

    const existingComment = activeMatterTask.comments.find(
      (comment) => comment.id === editingCommentId,
    );

    if (!existingComment) return;

    try {
      await updateMatterTaskComment.mutateAsync({
        ...annotationParentPayload,
        taskId: activeMatterTask.id,
        id: existingComment.id,
        message: getEditCommentBackendMessage(),
        dateModified: existingComment.dateModified,
      });
      setEditingCommentId(null);
      setEditCommentInput("");
      setEditCommentContent("");
    } catch {
      // Error handled via mutation toast
    }
  };

  const handleConfirmDeleteComment = async () => {
    if (!commentIdPendingDelete || !activeMatterTask || !matter) {
      return;
    }

    try {
      await deleteMatterTaskComment.mutateAsync({
        ...annotationParentPayload,
        id: commentIdPendingDelete,
      });
    } finally {
      setCommentIdPendingDelete(null);
    }
  };

  const handleDelete = () => {
    if (activeMatterTask && matter) {
      deleteMatterTask.mutate({
        ...annotationParentPayload,
        id: activeMatterTask.id,
      });
      setOpen(false);
    }
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const { notifyMe, assignerIds, assigneeIds, appAssigneeIds, ...taskData } =
      data;
    if (!matter) {
      toast.error("Select a matter to continue.");
      return;
    }
    if (editing) {
      if (!activeMatterTask) {
        throw new Error("Active matter task not found");
      }
      const res = await updateMatterTask.mutateAsync({
        ...annotationParentPayload,
        ...taskData,
        id: activeMatterTask.id,
        dateModified: activeMatterTask.dateModified,
        fileId: subItem?.type === "file" ? subItem.id : activeMatterTask.fileId,
        completedDate: isCompleted ? data.completedDate : null,
      });
      const connectionPromises: Promise<unknown>[] = [];
      const currentApps = new Set(
        activeMatterTask.assignees.flatMap((assignment) =>
          assignment.integrationAppId ? [assignment.integrationAppId] : [],
        ),
      );
      for (const integrationAppId of appAssigneeIds) {
        if (!currentApps.has(integrationAppId))
          connectionPromises.push(
            addAppAssignment.mutateAsync({
              role: "Assignee",
              taskIds: [activeMatterTask.id],
              integrationAppId,
            }),
          );
      }
      for (const integrationAppId of currentApps) {
        if (!appAssigneeIds.includes(integrationAppId))
          connectionPromises.push(
            removeAppAssignment.mutateAsync({
              role: "Assignee",
              taskIds: [activeMatterTask.id],
              integrationAppId,
            }),
          );
      }
      const currentAssigneeIds = new Set(
        activeMatterTask.assignees.flatMap((assignee) =>
          assignee.userId ? [assignee.userId] : [],
        ),
      );
      const nextAssigneeIds = new Set(assigneeIds);
      if (!massTortId) {
        activeMatterTask.assignees.forEach((assignee) => {
          if (assignee.userId && !nextAssigneeIds.has(assignee.userId)) {
            connectionPromises.push(
              deleteMatterUserConnection.mutateAsync({
                matterId: matter.id,
                id: assignee.id,
              }),
            );
          }
        });
        assigneeIds.forEach((userId) => {
          if (!currentAssigneeIds.has(userId)) {
            connectionPromises.push(
              createMatterUserConnection.mutateAsync({
                taskId: res.data.id,
                matterId: res.data.matterId,
                label: "Assignee",
                userId,
              }),
            );
          }
        });
      }

      const currentAssignerIds = new Set(
        activeMatterTask.assigners.flatMap((assigner) =>
          assigner.userId ? [assigner.userId] : [],
        ),
      );
      const nextAssignerIds = new Set(assignerIds);
      if (!massTortId) {
        activeMatterTask.assigners.forEach((assigner) => {
          if (assigner.userId && !nextAssignerIds.has(assigner.userId)) {
            connectionPromises.push(
              deleteMatterUserConnection.mutateAsync({
                matterId: matter.id,
                id: assigner.id,
              }),
            );
          }
        });
        assignerIds.forEach((userId) => {
          if (!currentAssignerIds.has(userId)) {
            connectionPromises.push(
              createMatterUserConnection.mutateAsync({
                taskId: res.data.id,
                matterId: res.data.matterId,
                label: "Assigner",
                userId,
              }),
            );
          }
        });
      }

      const currentNotifyConnections = activeMatterTask.notifiers.filter(
        (notifier) => notifier.userId === currentUser.id,
      );
      if (!massTortId && notifyMe && currentNotifyConnections.length === 0) {
        connectionPromises.push(
          createMatterUserConnection.mutateAsync({
            taskId: res.data.id,
            matterId: res.data.matterId,
            label: "Notify",
            userId: currentUser.id,
          }),
        );
      } else if (
        !massTortId &&
        !notifyMe &&
        currentNotifyConnections.length > 0
      ) {
        currentNotifyConnections.forEach((notifier) => {
          connectionPromises.push(
            deleteMatterUserConnection.mutateAsync({
              matterId: matter.id,
              id: notifier.id,
            }),
          );
        });
      }

      if (connectionPromises.length > 0) {
        setIsSavingConnections(true);
        try {
          await Promise.all(connectionPromises);
        } finally {
          setIsSavingConnections(false);
        }
      }
    } else {
      await createMatterTask.mutateAsync({
        ...annotationParentPayload,
        ...taskData,
        completedDate: isCompleted ? taskData.completedDate : undefined,
        phaseActionId:
          subItem?.type === "phase-action" ? subItem.id : undefined,
        fileId: subItem && subItem.type === "file" ? subItem.id : undefined,
        vehicleId:
          subItem && subItem.type === "vehicle" ? subItem.id : undefined,
        incidentReportId:
          subItem && subItem.type === "incident-report"
            ? subItem.id
            : undefined,
        insuranceId:
          subItem && subItem.type === "insurance" ? subItem.id : undefined,
        medicalTreatmentId:
          subItem && subItem.type === "medical-treatment"
            ? subItem.id
            : undefined,
        medicalTreatmentBillId:
          subItem && subItem.type === "medical-treatment-bill"
            ? subItem.id
            : undefined,
        medicalTreatmentAppointmentId:
          subItem && subItem.type === "medical-treatment-appointment"
            ? subItem.id
            : undefined,
        medicalTreatmentPlanId:
          subItem && subItem.type === "medical-treatment-plan"
            ? subItem.id
            : undefined,
        medicalTreatmentRecordId:
          subItem && subItem.type === "medical-treatment-record"
            ? subItem.id
            : undefined,
        medicalTreatmentBalanceConfirmationId:
          subItem && subItem.type === "medical-treatment-balance-confirmation"
            ? subItem.id
            : undefined,
        settlementDemandId:
          subItem && subItem.type === "settlement-demand"
            ? subItem.id
            : undefined,
        settlementOfferId:
          subItem && subItem.type === "settlement-offer"
            ? subItem.id
            : undefined,
        settlementCounterId:
          subItem && subItem.type === "settlement-counter"
            ? subItem.id
            : undefined,
        expenseId:
          subItem && subItem.type === "expense" ? subItem.id : undefined,
        lostWagesId:
          subItem && subItem.type === "lost-wage" ? subItem.id : undefined,
        lienId: subItem && subItem.type === "lien" ? subItem.id : undefined,
        propertyDamageId:
          subItem && subItem.type === "property-damage"
            ? subItem.id
            : undefined,
        signatureRequestId:
          subItem && subItem.type === "signature-request"
            ? subItem.id
            : undefined,
        trustCheckId:
          subItem && subItem.type === "trust-check" ? subItem.id : undefined,
        litigationDateId:
          subItem && subItem.type === "matter-litigation-date"
            ? subItem.id
            : undefined,
        statuteOfLimitationsId:
          subItem && subItem.type === "statute-of-limitations"
            ? subItem.id
            : undefined,
        emailId: subItem && subItem.type === "email" ? subItem.id : undefined,
        userConnections: massTortId
          ? undefined
          : [
              ...appAssigneeIds.map((integrationAppId) => ({
                label: "Assignee",
                integrationAppId,
              })),
              ...assigneeIds.map((userId) => ({
                label: "Assignee",
                userId,
              })),
              ...assignerIds.map((userId) => ({
                label: "Assigner",
                userId,
              })),
              ...(notifyMe
                ? [{ label: "Notify", userId: currentUser.id }]
                : []),
            ],
      });
      form.reset();
      toast.success("Matter task created successfully");
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
      setCommentInput("");
      setCommentContent("");
      setEditingCommentId(null);
      setEditCommentInput("");
      setEditCommentContent("");
      setCommentIdPendingDelete(null);
    }
  }, [
    open,
    form,
    setCommentContent,
    setEditingCommentId,
    setEditCommentInput,
    setEditCommentContent,
    setCommentIdPendingDelete,
  ]);

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          size="3xl"
          bodyContained={bodyContained}
          loading={isLoading}
          isDirty={form.formState.isDirty}
          bodyFormId={hasParentContext && matter ? taskModalFormId : undefined}
          bodyFormOnSubmit={
            hasParentContext && matter
              ? async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await form.handleSubmit(onSubmit)();
                }
              : undefined
          }
          footer={
            hasParentContext && matter ? (
              <div className="flex w-full flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  {hasDeletePermission && editing && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => {
                        setIsDeleting(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <FormCancelButton
                    setOpen={setOpen}
                    isDirty={form.formState.isDirty}
                  />
                  <FormSubmitButton
                    submitFormId={taskModalFormId}
                    title={editing ? "Save" : "Add task"}
                    disabled={isLoading}
                    overrideDisabled={Boolean(initialData)}
                  />
                </div>
              </div>
            ) : null
          }
        >
          {hasParentContext && isAnnotationParentError ? (
            <div className="flex min-h-[min(45vh,400px)] flex-col items-center justify-center gap-4 px-6 py-12 text-center">
              <Typography variant="heading" className="text-destructive">
                Could not load this matter
              </Typography>
              <Typography
                variant="subHeading"
                className="text-muted-foreground max-w-md"
              >
                Something went wrong while loading case details. Check your
                connection and try again, or pick a different matter.
              </Typography>
              {!editing && modalStackId ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    updateSlideoverPayload(modalStackId, { matterId: "" });
                  }}
                >
                  Choose a different matter
                </Button>
              ) : null}
            </div>
          ) : null}
          {hasParentContext &&
          !isAnnotationParentError &&
          isAwaitingParentDetail ? (
            <div className="flex min-h-[min(45vh,400px)] flex-col items-center justify-center gap-4 px-6 py-12">
              <LoadingSpinner className="h-10 w-10" />
              <Typography variant="heading" className="text-center text-base">
                Loading matter
              </Typography>
              <Typography
                variant="subHeading"
                className="text-muted-foreground max-w-md text-center"
              >
                Hang tight while we load this case so you can finish creating
                your task.
              </Typography>
            </div>
          ) : null}
          {isMatterPickerStep && modalStackId ? (
            <MatterTaskCreateMatterPicker modalStackId={modalStackId} />
          ) : null}
          {isMatterPickerStep && !modalStackId ? (
            <div className="flex min-h-[min(40vh,320px)] w-full flex-col items-center justify-center px-6 py-12 text-center">
              <div className="max-w-md">
                <Typography variant="heading">Select a matter</Typography>
                <Typography variant="subHeading" className="mt-2">
                  Matter selection is unavailable in this context. Open a matter
                  and create a task from the matter&apos;s Tasks tab instead.
                </Typography>
              </div>
            </div>
          ) : null}
          {hasParentContext && matter && !isAnnotationParentError ? (
            <>
              <fieldset
                disabled={editing ? !canEdit : false}
                className={slideoverFieldsetClassName}
              >
                {!hideMatterField && (
                  <div className="col-span-2">
                    <Typography size="sm" weight="medium">
                      Matter
                    </Typography>
                    <div className="bg-muted/30 mt-2 rounded-md border px-3 py-2">
                      <Typography size="sm">
                        {matter.title || "Unknown Matter"}
                      </Typography>
                    </div>
                  </div>
                )}
                <TextFormField
                  register={form.register("title")}
                  label="Task title*"
                />
                <SimpleSelectFormField
                  items={MATTER_TASK_STATUS_OPTIONS}
                  register={form.register("status")}
                  label="Task status"
                  onChange={(value) => {
                    if (value === "Completed") {
                      form.setValue("completedDate", dayjs().toISOString());
                    }
                  }}
                />
                <MultiselectFormField
                  options={taskUserOptions}
                  {...userDirectory.selectProps}
                  placeholder="Select assigners"
                  label="Assigner"
                  register={form.register("assignerIds")}
                  disabled={!canCreateMatterUserConnections}
                />
                <MultiselectFormField
                  options={taskUserOptions}
                  {...userDirectory.selectProps}
                  placeholder="Select assignees"
                  label="Assignee"
                  register={form.register("assigneeIds")}
                  disabled={!canCreateMatterUserConnections}
                />
                {!massTortId && (
                  <MultiselectFormField
                    options={[
                      ...assignmentApps,
                      ...(activeMatterTask?.assignees ?? []).flatMap(
                        ({ integrationAppId, integrationAppName }) =>
                          integrationAppId &&
                          !assignmentApps.some(
                            (app) => app.integrationAppId === integrationAppId,
                          )
                            ? [
                                {
                                  integrationAppId,
                                  integrationAppName:
                                    integrationAppName ?? "Unavailable app",
                                },
                              ]
                            : [],
                      ),
                    ].map((app) => ({
                      value: app.integrationAppId,
                      label: `${app.integrationAppName ?? "App"} (app)`,
                    }))}
                    placeholder="Select installed apps"
                    label="App assignees"
                    register={form.register("appAssigneeIds")}
                    disabled={!canCreateMatterUserConnections}
                  />
                )}
                <div className="col-span-full">
                  <div className="w-full sm:w-1/2">
                    <CheckboxFormField
                      label="Notify me"
                      register={form.register("notifyMe")}
                      disabled={!canCreateMatterUserConnections}
                    />
                  </div>
                </div>
                <SimpleSelectFormField
                  items={MATTER_TASK_PRIORITY_OPTIONS}
                  register={form.register("priority")}
                  label="Priority"
                  placeholder="Select a priority"
                  clearable
                />
                <DateFormField
                  register={form.register("startDate")}
                  name="startDate"
                  label="Start date"
                />
                <DateFormField
                  register={form.register("dueDate")}
                  name="dueDate"
                  label="Due date"
                />
                {isCompleted && (
                  <DateFormField
                    register={form.register("completedDate")}
                    name="completedDate"
                    label="Completed date"
                  />
                )}
                <div className="col-span-2">
                  <TextAreaFormField
                    register={form.register("description")}
                    label="Task description"
                    textAreaClassName="field-sizing-content max-h-[20lh]"
                  />
                </div>
              </fieldset>
              {editing && (
                <section className="dark:bg-muted/20 bg-slate-50/60 p-6">
                  <header className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-foreground text-sm font-semibold">
                        Task comments
                      </h3>
                    </div>
                    <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs font-medium">
                      {commentCount}
                    </span>
                  </header>
                  <div className="mt-6 flex flex-col gap-3">
                    {sortedComments.map((comment) => {
                      const commentUser = users.find(
                        (user) => user.id === comment.userId,
                      );
                      const commentAuthor = comment.integrationAppId
                        ? {
                            id: comment.integrationAppId,
                            firstName:
                              comment.integrationAppName?.trim() ||
                              "Integration app",
                            lastName: "",
                          }
                        : commentUser;
                      const commentAuthorLabel = comment.integrationAppId
                        ? comment.integrationAppName?.trim() ||
                          "Integration app"
                        : (formatUsersName({
                            firstName: commentUser?.firstName ?? "",
                            lastName: commentUser?.lastName ?? "",
                          }) ?? "Unknown User");
                      const isOwnComment = comment.userId === currentUser.id;
                      const canEditComment =
                        isOwnComment || canEditOtherComments;
                      const canDeleteComment =
                        isOwnComment || canDeleteOtherComments;
                      const isEditingThisComment =
                        editingCommentId === comment.id;

                      return (
                        <article
                          key={comment.id}
                          className="border-border bg-card flex gap-3 rounded-xl border p-4 shadow-sm"
                        >
                          <UserProfilePhoto
                            user={{
                              id: commentAuthor?.id ?? "",
                              firstName: commentAuthor?.firstName ?? "",
                              lastName: commentAuthor?.lastName ?? "",
                              profilePicturePresignedUrlKey:
                                commentUser?.profilePicturePresignedUrlKey,
                            }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-xs">
                                <span className="text-foreground font-medium">
                                  {commentAuthorLabel}
                                </span>
                                <span className="text-muted-foreground">•</span>
                                <span>
                                  {formatDateTime(
                                    convertToLocalFromDatabaseDateTime(
                                      comment.dateCreated,
                                    ),
                                    false,
                                  )}
                                </span>
                              </div>
                              {(canEditComment || canDeleteComment) && (
                                <div className="flex gap-1">
                                  {!isEditingThisComment && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => {
                                        handleStartEditComment(comment);
                                      }}
                                      aria-label="Edit comment"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canDeleteComment && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="text-destructive hover:text-destructive h-7 w-7"
                                      onClick={() => {
                                        setCommentIdPendingDelete(comment.id);
                                      }}
                                      aria-label="Delete comment"
                                      disabled={
                                        deleteMatterTaskComment.isPending
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                            {isEditingThisComment ? (
                              <div className="border-border bg-card mt-2 rounded-md border shadow-sm">
                                <EditorContent
                                  editor={editCommentEditor}
                                  className="max-h-48 overflow-y-auto"
                                  onKeyDown={(event) => {
                                    if (
                                      event.key !== "Enter" ||
                                      event.shiftKey ||
                                      event.altKey ||
                                      event.metaKey ||
                                      event.ctrlKey
                                    ) {
                                      return;
                                    }

                                    if (
                                      editMentionDropdownOpenRef.current ||
                                      !canSaveEditedComment
                                    ) {
                                      return;
                                    }

                                    event.preventDefault();
                                    event.stopPropagation();
                                    void handleSaveEditedComment();
                                  }}
                                />
                                <div className="flex items-center justify-end gap-2 px-4 py-2.5">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancelEditComment}
                                    disabled={updateMatterTaskComment.isPending}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      void handleSaveEditedComment();
                                    }}
                                    disabled={!canSaveEditedComment}
                                  >
                                    Update
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-2">
                                <TaskCommentText
                                  text={comment.message}
                                  mentionedApps={comment.mentionedApps}
                                  users={users}
                                />
                              </div>
                            )}
                          </div>
                        </article>
                      );
                    })}
                    <div className="border-border bg-card relative flex-1 overflow-hidden rounded-xl border shadow-sm">
                      <EditorContent
                        editor={commentEditor}
                        className="max-h-48 overflow-y-auto"
                        onKeyDown={(event) => {
                          if (
                            event.key !== "Enter" ||
                            event.shiftKey ||
                            event.altKey ||
                            event.metaKey ||
                            event.ctrlKey
                          ) {
                            return;
                          }

                          if (mentionDropdownOpenRef.current) {
                            return;
                          }

                          if (!commentInput.trim() || !canAddComment) {
                            return;
                          }

                          event.preventDefault();
                          event.stopPropagation();
                          void handleAddComment();
                        }}
                      />
                      <div className="flex items-center justify-end gap-2 px-4 py-2.5">
                        <Button
                          type="button"
                          variant="default"
                          disabled={!commentInput.trim() || !canAddComment}
                          onClick={handleAddComment}
                        >
                          Add comment
                        </Button>
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </>
          ) : null}
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this task?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
      <ActionConfirmationModal
        title="Are you sure you want to delete this comment?"
        open={Boolean(commentIdPendingDelete)}
        setOpen={(open) => {
          if (!open) {
            setCommentIdPendingDelete(null);
          }
        }}
        afterActionFn={() => {
          void handleConfirmDeleteComment();
        }}
      />
    </>
  );
};
